import { useEffect, useMemo, useState } from "react";
import { api, type Board, type Task } from "../../api.ts";
import { tokens, useTheme } from "../../glassTheme.tsx";
import { Sheet } from "../glass/Glass.tsx";
import { GlassButton } from "../glass/Button.tsx";
import { TierDot } from "../sessions/Row.tsx";
import { shortDate } from "../../theme.ts";

type Full = Task & {
  blockers: Task[]; unblocking: Task[]; flags: { id: string; body: string | null }[];
  history: { id: string | number; date: string; kind: string; text: string | null }[];
};

export type DetailMode =
  | { kind: "edit"; id: string }
  | { kind: "create"; value_id: string | null; area_id: string | null };

const isMisc = (valueId: string | null) => valueId === "MISC";

/**
 * The task detail sheet (§2) — also what "+add" opens, empty, per the same
 * section: title is the only required field, and the sheet becomes the real
 * task in place once it's created, rather than closing and reopening.
 *
 * Not built: editing dependencies (blocked-by/unblocks are tappable rows
 * here, same as the spec asks, but there's no add/remove-a-blocker UI —
 * the row spec only asks for the rows, not an editor for them) and a
 * cascading area picker (value picks straight to a flat area list, not the
 * two-level "area inside a value" the spec's Board tab describes — same
 * simplification, disclosed in the completion report).
 */
export function TaskDetail({
  mode, board, onClose, onChanged, onOpen, onDiveIn,
}: {
  mode: DetailMode; board: Board;
  onClose: () => void; onChanged: () => void;
  onOpen: (id: string) => void;
  onDiveIn: (task: { id: string; title: string }) => void;
}) {
  const { colors } = useTheme();
  const [t, setT] = useState<Full | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickingValue, setPickingValue] = useState(false);
  const [parking, setParking] = useState(false);
  const [because, setBecause] = useState("");

  // create-mode local state
  const [title, setTitle] = useState("");
  const [valueId, setValueId] = useState<string | null>(mode.kind === "create" ? mode.value_id : null);
  const [areaId, setAreaId] = useState<string | null>(mode.kind === "create" ? mode.area_id : null);
  const [tier, setTier] = useState<Task["tier"]>("should");
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (mode.kind !== "edit") return;
    let live = true;
    setT(null); setError(null);
    api.task(mode.id).then((x) => live && setT(x as Full)).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [mode.kind === "edit" ? mode.id : "create"]);

  const titleById = useMemo(() => new Map(board.tasks.map((x) => [x.id, x.title])), [board.tasks]);
  const areasFor = (v: string | null) => board.areas.filter((a) => a.values.some((av) => av.value_id === v));

  const patch = async (p: Record<string, unknown>) => {
    if (!t) return;
    const before = t;
    setT({ ...t, ...p } as Full);
    try { await api.update(t.id, p); onChanged(); }
    catch (e: any) { setT(before); setError(e.message); }
  };

  const submitCreate = async () => {
    if (!title.trim() || !valueId || busy) return;
    setBusy(true); setError(null);
    try {
      const created = await api.create({
        title: title.trim(), tier, value_id: valueId, area_id: areaId,
        ...(isMisc(valueId) || !why.trim() ? {} : { why: why.trim() }),
      });
      onChanged();
      onOpen(created.id);
    } catch (e: any) { setError(e?.message ?? "that did not go through"); }
    finally { setBusy(false); }
  };

  const submitPark = async () => {
    if (!t || !because.trim() || busy) return;
    setBusy(true); setError(null);
    try { await api.park(t.id, because.trim()); onChanged(); onClose(); }
    catch (e: any) { setError(e?.message ?? "that did not go through"); }
    finally { setBusy(false); }
  };

  const label = (s: string) => (
    <div style={{
      fontFamily: tokens.font.sans, fontSize: tokens.type.caption.size,
      letterSpacing: tokens.type.caption.letterSpacing, textTransform: "uppercase",
      fontWeight: 600, color: colors.ink2, margin: "18px 0 6px",
    }}>{s}</div>
  );

  const inputStyle: React.CSSProperties = {
    width: "100%", boxSizing: "border-box", fontFamily: tokens.font.sans, fontSize: 15,
    padding: "9px 11px", borderRadius: 8, border: `1px solid ${colors.hair2}`,
    background: colors.sheet, color: colors.ink,
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(30,25,20,0.4)", display: "flex", alignItems: "flex-end", zIndex: 40 }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 560, margin: "0 auto" }}>
        <Sheet style={{
          borderRadius: "22px 22px 0 0", padding: `18px 18px calc(24px + env(safe-area-inset-bottom))`,
          maxHeight: "min(88dvh, calc(100dvh - 24px - env(safe-area-inset-top)))", boxSizing: "border-box", overflowY: "auto", overscrollBehavior: "contain",
        }}>
          {error && <p style={{ margin: "0 0 10px", fontSize: 12.5, color: "#9C4A3C" }}>{error}</p>}

          {mode.kind === "create" ? (
            <div>
              <div style={{
                fontFamily: tokens.font.sans, fontSize: tokens.type.caption.size,
                letterSpacing: tokens.type.caption.letterSpacing, textTransform: "uppercase",
                fontWeight: 600, color: colors.ink2,
              }}>New task</div>

              <input
                autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="Title"
                style={{
                  width: "100%", boxSizing: "border-box", fontFamily: tokens.font.serif, fontSize: tokens.type.sheetTitle.size,
                  padding: "8px 0", border: "none", borderBottom: `1px solid ${colors.hair2}`, background: "none", color: colors.ink,
                  margin: "6px 0 4px",
                }}
              />

              {label("Value")}
              <select
                value={valueId ?? ""} onChange={(e) => { setValueId(e.target.value || null); setAreaId(null); }}
                style={inputStyle}
              >
                <option value="" disabled>choose a value</option>
                {board.values.map((v) => <option key={v.key} value={v.key}>{v.name}</option>)}
              </select>

              {valueId && areasFor(valueId).length > 0 && (
                <>
                  {label("Area")}
                  <select value={areaId ?? ""} onChange={(e) => setAreaId(e.target.value || null)} style={inputStyle}>
                    <option value="">no particular area</option>
                    {areasFor(valueId).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </>
              )}

              {label("Tier")}
              <TierPicker tier={tier} onChange={setTier} />

              {!isMisc(valueId) && (
                <>
                  {label("Why")}
                  <input value={why} onChange={(e) => setWhy(e.target.value)} placeholder="why this, optional" style={inputStyle} />
                </>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
                <GlassButton variant="primary" disabled={!title.trim() || !valueId || busy} onClick={submitCreate}>
                  {busy ? "…" : "Add"}
                </GlassButton>
                <GlassButton variant="ghost" onClick={onClose}>Cancel</GlassButton>
              </div>
            </div>
          ) : !t ? (
            <p style={{ color: colors.ink2, fontSize: 13, margin: 0 }}>{error ? "" : "Opening…"}</p>
          ) : (
            <div>
              <button
                onClick={() => setPickingValue((x) => !x)}
                style={{
                  display: "block", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left",
                  fontFamily: tokens.font.sans, fontSize: 12.5, color: colors.ink2,
                }}
              >{[t.value_name, t.area_name].filter(Boolean).join(" › ") || "Misc"}</button>

              {pickingValue && (
                <div style={{ margin: "8px 0", padding: "10px", border: `1px solid ${colors.hair}`, borderRadius: 10 }}>
                  <select
                    value={t.value_id ?? ""}
                    onChange={(e) => { patch({ value_id: e.target.value || null, area_id: null }); }}
                    style={{ ...inputStyle, marginBottom: 8 }}
                  >
                    {board.values.map((v) => <option key={v.key} value={v.key}>{v.name}</option>)}
                  </select>
                  {areasFor(t.value_id).length > 0 && (
                    <select
                      value={t.area_id ?? ""}
                      onChange={(e) => patch({ area_id: e.target.value || null })}
                      style={inputStyle}
                    >
                      <option value="">no particular area</option>
                      {areasFor(t.value_id).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  )}
                </div>
              )}

              <EditableTitle title={t.title} onSave={(v) => patch({ title: v })} />

              {t.why_chain.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {t.why_chain.map((w, i) => (
                    <p key={i} style={{
                      margin: "0 0 4px", fontSize: 13, lineHeight: 1.5,
                      color: colors.ink2,
                    }}>{w}</p>
                  ))}
                </div>
              )}

              {label("Next")}
              <EditableLine value={t.next ?? ""} placeholder="the one physical action" onSave={(v) => patch({ next: v || null })} />

              {label("When")}
              {t.state === "parked" ? (
                <input
                  value={t.review_when ?? ""} onChange={(e) => setT({ ...t, review_when: e.target.value })}
                  onBlur={() => patch({ review_when: t.review_when || null })}
                  placeholder="parked until…" style={inputStyle}
                />
              ) : (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="date" value={t.due ?? ""} onChange={(e) => patch({ due: e.target.value || null })}
                    style={{ ...inputStyle, width: "auto" }}
                  />
                  {t.due && <button onClick={() => patch({ due: null })} style={linkButton(colors.ink2)}>no date</button>}
                </div>
              )}

              {label("Tier")}
              <TierPicker tier={t.tier} onChange={(v) => patch({ tier: v })} />

              <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
                <div style={{ flex: 1 }}>
                  {label("Minutes")}
                  <input
                    type="number" min={0} value={t.mins ?? ""} onChange={(e) => setT({ ...t, mins: e.target.value ? Number(e.target.value) : null })}
                    onBlur={() => patch({ mins: t.mins })} style={inputStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  {label("Money")}
                  <input
                    value={t.money ?? ""} onChange={(e) => setT({ ...t, money: e.target.value })}
                    onBlur={() => patch({ money: t.money || null })} style={inputStyle}
                  />
                </div>
              </div>

              {label("Note")}
              <textarea
                value={t.note ?? ""} onChange={(e) => setT({ ...t, note: e.target.value })}
                onBlur={() => patch({ note: t.note || null })}
                rows={3} style={{ ...inputStyle, resize: "vertical" }}
              />

              {t.blockers.length > 0 && (<>
                {label("Waiting on")}
                {t.blockers.map((b) => (
                  <RowLink key={b.id} label={b.title} onClick={() => onOpen(b.id)} />
                ))}
              </>)}

              {t.unblocking.length > 0 && (<>
                {label("Done, this opens")}
                {t.unblocking.map((b) => (
                  <RowLink key={b.id} label={b.title} onClick={() => onOpen(b.id)} />
                ))}
              </>)}

              {t.flags.length > 0 && (<>
                {label("Flagged")}
                {t.flags.map((f) => (
                  <p key={f.id} style={{ margin: "0 0 4px", fontSize: 13, color: colors.ink2, lineHeight: 1.5 }}>{f.body}</p>
                ))}
              </>)}

              {t.history.length > 0 && (<>
                {label("Log")}
                {t.history.map((h) => (
                  <div key={h.id} style={{ padding: "6px 0", borderBottom: `1px solid ${colors.hair}` }}>
                    <div style={{ fontSize: 11, color: colors.ink2 }}>{shortDate(h.date)} · {h.kind}</div>
                    {h.text && <div style={{ fontSize: 13, color: colors.ink }}>{h.text}</div>}
                  </div>
                ))}
              </>)}

              {parking ? (
                <div style={{ marginTop: 18 }}>
                  {label("Why it's being parked")}
                  <input
                    autoFocus value={because} onChange={(e) => setBecause(e.target.value)}
                    placeholder="what makes it not now" style={inputStyle}
                  />
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <GlassButton variant="primary" disabled={!because.trim() || busy} onClick={submitPark}>Park it</GlassButton>
                    <GlassButton variant="ghost" onClick={() => setParking(false)}>Back</GlassButton>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
                  {t.state !== "done" ? (
                    <GlassButton variant="primary" onClick={async () => { await patch({ state: "done" }); onClose(); }}>Done</GlassButton>
                  ) : (
                    <GlassButton variant="default" onClick={() => patch({ state: "open" })}>Reopen</GlassButton>
                  )}
                  {t.state !== "parked" && <GlassButton variant="default" onClick={() => setParking(true)}>Park</GlassButton>}
                  {t.state === "parked" && (
                    <GlassButton variant="default" onClick={() => patch({ state: "open", parked_because: null })}>Unpark</GlassButton>
                  )}
                  <GlassButton variant="ghost" onClick={() => onDiveIn({ id: t.id, title: t.title })}>Dive in</GlassButton>
                </div>
              )}
            </div>
          )}
        </Sheet>
      </div>
    </div>
  );
}

function linkButton(color: string): React.CSSProperties {
  return {
    fontFamily: tokens.font.sans, fontSize: 12.5, color, background: "none",
    border: "none", padding: 0, cursor: "pointer", textDecoration: "underline",
  };
}

function RowLink({ label, onClick }: { label: string; onClick: () => void }) {
  const { colors } = useTheme();
  return (
    <div
      onClick={onClick} role="button" tabIndex={0}
      style={{ padding: "8px 0", borderBottom: `1px solid ${colors.hair}`, fontSize: 14, color: colors.ink, cursor: "pointer" }}
    >{label}</div>
  );
}

function TierPicker({ tier, onChange }: { tier: Task["tier"]; onChange: (t: "must" | "should" | "nice") => void }) {
  const { colors } = useTheme();
  const options: { key: "must" | "should" | "nice"; label: string }[] = [
    { key: "must", label: "Must" }, { key: "should", label: "Should" }, { key: "nice", label: "Nice" },
  ];
  return (
    <div style={{ display: "flex", gap: 18 }}>
      {options.map((o) => (
        <button
          key={o.key} onClick={() => onChange(o.key)}
          style={{
            display: "flex", alignItems: "center", background: "none", border: "none", cursor: "pointer",
            fontFamily: tokens.font.sans, fontSize: 14, color: tier === o.key ? colors.ink : colors.ink2, padding: 0,
          }}
        ><TierDot tier={o.key} />{o.label}</button>
      ))}
    </div>
  );
}

/** Serif title, edit inline on tap — no separate edit mode (§2). */
function EditableTitle({ title, onSave }: { title: string; onSave: (v: string) => void }) {
  const { colors } = useTheme();
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(title);
  useEffect(() => setV(title), [title]);

  if (editing) {
    return (
      <input
        autoFocus value={v} onChange={(e) => setV(e.target.value)}
        onBlur={() => { setEditing(false); if (v.trim() && v !== title) onSave(v.trim()); else setV(title); }}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
        style={{
          width: "100%", boxSizing: "border-box", fontFamily: tokens.font.serif, fontSize: tokens.type.sheetTitle.size,
          lineHeight: tokens.type.sheetTitle.lineHeight, padding: "4px 0", margin: "6px 0", border: "none",
          borderBottom: `1px solid ${colors.hair2}`, background: "none", color: colors.ink,
        }}
      />
    );
  }
  return (
    <div
      onClick={() => setEditing(true)}
      style={{
        fontFamily: tokens.font.serif, fontSize: tokens.type.sheetTitle.size, lineHeight: tokens.type.sheetTitle.lineHeight,
        margin: "6px 0", color: colors.ink, cursor: "text",
      }}
    >{title}</div>
  );
}

function EditableLine({ value, placeholder, onSave }: { value: string; placeholder: string; onSave: (v: string) => void }) {
  const { colors } = useTheme();
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);

  if (editing) {
    return (
      <input
        autoFocus value={v} onChange={(e) => setV(e.target.value)}
        onBlur={() => { setEditing(false); if (v !== value) onSave(v.trim()); }}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
        placeholder={placeholder}
        style={{
          width: "100%", boxSizing: "border-box", fontFamily: tokens.font.sans, fontSize: 15,
          padding: "9px 11px", borderRadius: 8, border: `1px solid ${colors.hair2}`, background: colors.sheet, color: colors.ink,
        }}
      />
    );
  }
  return (
    <p
      onClick={() => setEditing(true)}
      style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: value ? colors.ink : colors.ink2, cursor: "text" }}
    >{value || placeholder}</p>
  );
}
